import React from 'react';
import { Palette } from 'Palette';
import { MediaURL } from './imageCarouselTypes';
import { ContentProps } from '../../models/TopLevelTemplateComponentProps';
import AssetImage from '../AssetImage';
import StyledSection from '../styles/StyledSection';

export interface HeroImageProps {
  imageUrl?: MediaURL;
  altText?: ContentProps;
  id?: string;
  tone?: keyof typeof Palette;
  firstElement?: boolean;
  printMode?: boolean;
}

/** A hero image has no card around it but keeps the image cards' rounded corners. */
export const HERO_IMAGE_RADIUS = '0.625rem';

/**
 * Sized from the image's own pixels: `width: auto` shows it at its natural
 * width and `max-width: 100%` shrinks it to fit a narrower section, so it is
 * never stretched past its resolution and always keeps its aspect ratio.
 */
export const heroImageSx = {
  display: 'block',
  mx: 'auto',
  width: 'auto',
  maxWidth: '100%',
  height: 'auto',
  borderRadius: HERO_IMAGE_RADIUS,
};

/**
 * With the pixel size saved on the image, the section reserves its exact space
 * before it loads; without it (older blocks) the loaded image sizes itself.
 */
export function heroImageSxFor(imageUrl?: MediaURL) {
  const { width, height } = imageUrl ?? {};
  if (!width || !height) return heroImageSx;
  return { ...heroImageSx, width: `${width}px`, aspectRatio: `${width} / ${height}` };
}

export function heroImageIsSet(imageUrl?: MediaURL): boolean {
  const url = imageUrl?.url?.trim();
  return !!url && !url.includes('placeholder');
}

/** A single image on its own, centered — no heading, caption or card. */
export default function HeroImage({
  imageUrl,
  altText,
  id,
  tone = 'tone1',
  firstElement,
  printMode,
}: HeroImageProps) {
  if (!heroImageIsSet(imageUrl)) return null;
  return (
    <StyledSection
      id={id}
      noSidePaddingFrom="xs"
      noTopPadding={!firstElement}
      tone={tone}
      additionalClassName="hero-image-section content-management-item"
    >
      <AssetImage
        url={imageUrl!.url}
        alt={altText?.content ?? ''}
        className="hero-image"
        printMode={printMode}
        sx={heroImageSxFor(imageUrl)}
      />
    </StyledSection>
  );
}
